/**
 * @module types/settings/statistics
 * @description 表格统计值配置（存储字段名 `statistics`）
 */

export interface IStatisticsValue {
  avg: string[];
  avgNotNull: string[];
  max: string[];
  min: string[];
  sum: string[];
}

export const STATISTICS_KEYS = [
  'avg',
  'avgNotNull',
  'max',
  'min',
  'sum',
] as const;

export type TStatisticsKey = (typeof STATISTICS_KEYS)[number];

const STATISTICS_EMPTY_AVG: string[] = Object.freeze([]) as string[];
const STATISTICS_EMPTY_AVG_NOT_NULL: string[] = Object.freeze([]) as string[];
const STATISTICS_EMPTY_MAX: string[] = Object.freeze([]) as string[];
const STATISTICS_EMPTY_MIN: string[] = Object.freeze([]) as string[];
const STATISTICS_EMPTY_SUM: string[] = Object.freeze([]) as string[];

/**
 * statistics 缺省：各统计类型均未选指标
 *
 * @stable 须与 STRUCTURED_SETTING_DEFAULTS.statistics 为同一引用语义
 */
export const STATISTICS_DEFAULT: IStatisticsValue = Object.freeze({
  avg: STATISTICS_EMPTY_AVG,
  avgNotNull: STATISTICS_EMPTY_AVG_NOT_NULL,
  max: STATISTICS_EMPTY_MAX,
  min: STATISTICS_EMPTY_MIN,
  sum: STATISTICS_EMPTY_SUM,
}) as IStatisticsValue;

/** 是否存在任意统计项勾选 */
export function hasStatisticsSelection(stats: IStatisticsValue): boolean {
  return STATISTICS_KEYS.some(key => (stats[key]?.length || 0) > 0);
}

/**
 * 兼容历史：空对象 / 缺字段 → 归一为带五类空数组的结构
 */
export function normalizeStatistics(raw: unknown): IStatisticsValue {
  if (raw === STATISTICS_DEFAULT) {
    return STATISTICS_DEFAULT;
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return STATISTICS_DEFAULT;
  }
  const record = raw as Record<string, unknown>;
  if (Object.keys(record).length === 0) {
    return STATISTICS_DEFAULT;
  }
  return {
    avg: Array.isArray(record.avg)
      ? (record.avg as string[])
      : STATISTICS_DEFAULT.avg,
    avgNotNull: Array.isArray(record.avgNotNull)
      ? (record.avgNotNull as string[])
      : STATISTICS_DEFAULT.avgNotNull,
    max: Array.isArray(record.max)
      ? (record.max as string[])
      : STATISTICS_DEFAULT.max,
    min: Array.isArray(record.min)
      ? (record.min as string[])
      : STATISTICS_DEFAULT.min,
    sum: Array.isArray(record.sum)
      ? (record.sum as string[])
      : STATISTICS_DEFAULT.sum,
  };
}
